import { checkTokenResponse } from './check-token-response';
import type { AuthClientConfig } from './config';
import { GetTokenError, NotLoggedInError } from './errors';
import { getUserFromToken } from './get-user-from-token';
import { applyRedirectURI, getURLForPath } from './paths';
import {
  type AuthToken,
  type AuthTokenStorage,
  createTokenStorage,
} from './storage';
import type { UserInfo } from './user-info';

/**
 * Proactively refresh the access token if it is expiring within the next minute.
 * As a reference point, Google's auth library for Node.js uses 5 minutes:
 * https://github.com/googleapis/google-auth-library-nodejs/blob/a95b8272197f35453d1647eed85be475d51474c9/src/auth/authclient.ts#L156
 */
const REFRESH_THRESHOLD_MS = 1 * 60 * 1000;

export interface AuthClient {
  tokenStorage: AuthTokenStorage;

  /** Returns true if the user is logged in, false otherwise. */
  isLoggedIn: () => boolean;

  /** Returns true if the stored token expires within the refresh threshold. */
  isTokenExpiringSoon: () => boolean;

  /** Initiates a login using an optional url to redirect back into. */
  login: (backTo?: string) => void;

  /** Initiates a logout. */
  logout: () => void;

  /** Exchanges an authentication code for an access token. */
  getAndCacheToken: () => Promise<AuthToken>;

  /** Exchanges a short-lived authorization code for fresh tokens. */
  exchangeAuthCode: (code: string) => Promise<AuthToken>;

  /** Uses the stored refresh token to get a new access token. */
  refreshAccessToken: () => Promise<AuthToken>;

  /** Initiates a registration using an optional url to redirect back into. */
  register: (backTo?: string) => void;

  /** Retrieves user information. */
  getUserInfo: () => UserInfo | undefined;
}

export enum AuthStatus {
  Loading = 'loading',
  LoggedOut = 'loggedOut',
  LoggedIn = 'loggedIn',
  Unauthenticated = 'unauthenticated',
}

export interface AuthLoadingState {
  status: AuthStatus.Loading;
}

export interface AuthLoggedOutState {
  status: AuthStatus.LoggedOut;
}

export interface AuthLoggedInState {
  status: AuthStatus.LoggedIn;
  user: UserInfo;
}

export type AuthState =
  | AuthLoadingState
  | AuthLoggedOutState
  | AuthLoggedInState;

export const createAuthClient = (config: AuthClientConfig): AuthClient => {
  const { clientID, audience } = config;
  const tokenStorage = createTokenStorage(clientID);
  const serverURL = window.location.origin;

  const getExpiryTime = (): number | undefined => {
    const token = tokenStorage.retrieveToken();
    if (!token) {
      return undefined;
    }

    const expiryDate = new Date(token.expiry);
    return expiryDate.getTime();
  };

  const isLoggedIn = () => {
    const expiresAt = getExpiryTime() ?? 0;
    return expiresAt > Date.now();
  };

  const isTokenExpiringSoon = (): boolean => {
    const expiresAt = getExpiryTime() ?? 0;
    return expiresAt - Date.now() < REFRESH_THRESHOLD_MS;
  };

  const login = () => {
    const redirect = applyRedirectURI(serverURL, 'login', {
      audience,
      backto: window.location.href,
    });
    window.location.assign(redirect);
  };

  const logout = () => {
    tokenStorage.removeToken();
    const redirect = applyRedirectURI(serverURL, 'logout', {
      audience,
    });
    window.location.assign(redirect);
  };

  const register = () =>
    window.location.assign(
      applyRedirectURI(serverURL, 'register', {
        audience,
        backto: window.location.href,
      })
    );

  const getAndCacheToken = async (): Promise<AuthToken> => {
    const existingToken = tokenStorage.retrieveToken();
    const url = getURLForPath(serverURL, 'token', { audience });
    const headers: { Authorization?: string } = existingToken
      ? { Authorization: `Bearer ${existingToken.accessToken}` }
      : {};

    const response = await fetch(url, { headers });
    const result = await checkTokenResponse(response, existingToken);

    if (new Date(result.expiry).getTime() <= Date.now()) {
      throw new NotLoggedInError();
    }

    tokenStorage.storeToken(result);
    return result;
  };

  /**
   * Exchange a short-lived authorization code for fresh auth tokens.
   * The code is obtained from a cross-app magic link and is single-use.
   */
  const exchangeAuthCode = async (code: string): Promise<AuthToken> => {
    const url = getURLForPath(serverURL, 'auth-code-exchange');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new NotLoggedInError();
    }

    const result = (await response.json()) as AuthToken;
    tokenStorage.storeToken(result);
    return result;
  };

  /**
   * Get a new access token using the refresh token.
   * If no refresh token is available (like in the case of
   * exchangeAuthCode above) continue to use the existing
   * access token until it expires.
   */
  const refreshAccessToken = async (): Promise<AuthToken> => {
    const existingToken = tokenStorage.retrieveToken();
    if (!existingToken) {
      throw new NotLoggedInError();
    }

    if (!existingToken.refreshToken) {
      if (isLoggedIn()) {
        return existingToken;
      }
      throw new NotLoggedInError();
    }

    const url = getURLForPath(serverURL, 'refresh');
    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${existingToken.refreshToken}` },
    });

    if (response.status === 400 || response.status === 401) {
      throw new NotLoggedInError();
    }

    if (!response.ok) {
      throw new GetTokenError();
    }

    const newToken = (await response.json()) as AuthToken;
    tokenStorage.storeToken(newToken);
    return newToken;
  };

  const getUserInfo = () => {
    if (!isLoggedIn()) {
      return undefined;
    }

    const token = tokenStorage.retrieveToken();
    if (!token) {
      return undefined;
    }

    return getUserFromToken(token.accessToken);
  };

  return {
    tokenStorage,
    isLoggedIn,
    isTokenExpiringSoon,
    login,
    logout,
    register,
    getAndCacheToken,
    exchangeAuthCode,
    refreshAccessToken,
    getUserInfo,
  };
};

export const initializeClient = async (
  client: AuthClient
): Promise<AuthLoggedOutState | AuthLoggedInState> => {
  try {
    await client.getAndCacheToken();
  } catch {
    try {
      await client.refreshAccessToken();
    } catch {
      client.tokenStorage.removeToken();
      return { status: AuthStatus.LoggedOut };
    }
  }

  const user = client.getUserInfo();
  return user
    ? { status: AuthStatus.LoggedIn, user }
    : { status: AuthStatus.LoggedOut };
};
